/**
 * CustomModes Style02 - Stitch v2: gold theme
 */
import { useState, useEffect, useMemo } from 'react'
import type { TraitControlProps } from './types'
import {
  resolveCustomModesFromMcp,
  resolveCurrentCustomModeValue,
  resolveCustomModeIcon,
  getTraitConfig,
} from './traitStyleConfig'
import './CustomModesStyle02.css'

export function CustomModesStyle02({
  trait,
  device,
  state,
  attributes,
  onCommand,
  disabled = false,
}: TraitControlProps) {
  const { icon } = getTraitConfig(trait ?? 'CustomModes')
  const endpoint = useMemo(() => {
    const sn = device?.deviceSN ?? device?.sn ?? ''
    return sn.includes('#') ? sn.split('#')[1] || '1' : '1'
  }, [device?.deviceSN, device?.sn])
  const resolved = useMemo(
    () =>
      resolveCustomModesFromMcp(
        attributes,
        device?.customControls as Record<string, unknown> | undefined,
        endpoint,
        'en',
        device?.traits
      ),
    [attributes, device?.customControls, device?.traits, endpoint]
  )
  const current = useMemo(
    () =>
      resolveCurrentCustomModeValue(
        state,
        resolved.modeName,
        resolved.options[0]?.modeValue ?? 'mode1'
      ),
    [state, resolved.modeName, resolved.options]
  )
  const normalizedCurrent = useMemo(() => {
    if (resolved.options.some((option) => option.modeValue === current)) return current
    return resolved.options[0]?.modeValue ?? current
  }, [current, resolved.options])
  const [value, setValue] = useState(normalizedCurrent)
  const modeTitle = resolved.modeName || 'Custom Modes'
  const modeIcon = resolveCustomModeIcon(resolved.modeName, icon)

  useEffect(() => {
    setValue(normalizedCurrent)
  }, [normalizedCurrent])

  return (
    <div className="trait-custommodes-style02 trait-stitch-v2-glass">
      <div className="trait-custommodes-style02__header">
        <span className="trait-custommodes-style02__title">{modeTitle}</span>
        <span className={`material-symbols-outlined trait-custommodes-style02__icon`}>{modeIcon}</span>
      </div>
      <div className="trait-custommodes-style02__grid">
        {resolved.options.map((modeOption) => (
          <button
            key={modeOption.id}
            type="button"
            className={`trait-custommodes-style02__btn ${value === modeOption.modeValue ? 'active' : ''}`}
            onClick={() => {
              if (disabled) return
              setValue(modeOption.modeValue)
              void onCommand('setCustomMode', {
                modeName: resolved.modeName,
                modeValue: modeOption.modeValue,
                value: modeOption.modeValue,
              })
            }}
            disabled={disabled}
          >
            {modeOption.label}
          </button>
        ))}
      </div>
    </div>
  )
}
